import React, { useState, useEffect } from 'react'
import { useAuth, useForm } from '../hooks'
import { apiUrl, API_ENDPOINTS } from '../Services/api'
import { ERROR_MESSAGES, ROUTES } from '../constants'
import {
  Alert,
  TextField,
  Container,
  Box,
  Button,
  Typography,
  Paper,
  Link,
  Divider,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ScoreBookLogo from '../Assets/scorebook.png'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

const LoginForm = () => {
  const { isAuthenticated, setTokenCookie } = useAuth()
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const { values, handleChange } = useForm({
    username: '',
    password: '',
    remember: false,
  })

  useEffect(() => {
    if (isAuthenticated) navigate(ROUTES.HOME)
  }, [isAuthenticated, navigate])

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')
    setLoading(true)

    const { username, password } = values
    const body = `grant_type=password&username=${encodeURIComponent(
      username
    )}&password=${encodeURIComponent(password)}`

    try {
      const res = await fetch(apiUrl(API_ENDPOINTS.TOKEN), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      })

      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || ERROR_MESSAGES.LOGIN_FAILED)
      }

      const data = await res.json()
      setTokenCookie(data.access_token)
    } catch (err) {
      setError(err.message || ERROR_MESSAGES.LOGIN_FAILED)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        padding: 2,
      }}
    >
      <Container maxWidth="xs">
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            borderRadius: 4, 
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
            <img src={ScoreBookLogo} alt="ScoreBook" style={{ height: 60, marginBottom: 16 }} />
            <Typography variant="h5" component="h1" fontWeight="700" gutterBottom>
              Bon retour
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Connectez-vous pour retrouver vos livres et continuer votre lecture.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            autoComplete="off"
          >
            <TextField
              fullWidth
              label="E-mail ou Pseudo"
              placeholder="vous@exemple.com"
              variant="outlined"
              margin="normal"
              name="username"
              value={values.username}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Mot de passe"
              placeholder="Votre mot de passe"
              variant="outlined"
              margin="normal"
              name="password"
              type="password"
              value={values.password}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="remember"
                    checked={values.remember}
                    onChange={handleChange}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="body2">Se souvenir de moi</Typography>}
              />

              <Link href="#" underline="hover" variant="body2">
                Mot de passe oublié ?
              </Link>
            </Box>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              fullWidth
              size="large"
              sx={{ py: 1.5, borderRadius: 2 }}
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">OU</Typography>
          </Divider>

          <Typography variant="body2" align="center">
            Pas encore de compte ?{' '}
            <Link href="/creation" underline="hover" fontWeight="600">
              Créer un compte
            </Link>
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

export default LoginForm
